// Client-side mirror of the server rules for the public submission form
// (backend: validate_pengajuan in server.py). The server stays the source of truth.

export const MAX_HARI_KE_DEPAN = 180;

// Longest Nama Pasien; keep equal to NAMA_PASIEN_MAX in backend/server.py (test_nama_pasien_max.py checks it).
export const NAMA_PASIEN_MAX = 35;
// The counter turns red from this many characters on, as a warning before the limit.
export const NAMA_PASIEN_WARN = NAMA_PASIEN_MAX - 5;
export const namaPasienPanjangMsg = (n) =>
  `Nama Pasien maksimal ${NAMA_PASIEN_MAX} karakter (saat ini ${n}). Isi nama pasien saja, bukan diagnosa atau catatan lain.`;

const NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ .,'()-]{2,99}$/;
const RM_RE = /^\d{3}-\d{2}-\d{2}$/;
// Phone numbers: digits only (no spaces, "+", "-" or letters), at most 12 digits.
export const PHONE_MAX_DIGITS = 12;
const HP_DOKTER_RE = /^08[1-9]\d{7,9}$/;   // mobile, 10-12 digits
const TELP_RE = /^0\d{7,11}$/;            // mobile or landline, 8-12 digits
// Loose syntax check (one @, a dotted domain, no spaces); the server's email-validator has the final say.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_FORMAT_MSG = "Email Dokter Pengaju tidak valid (contoh: nama@domain.com)";
export const isValidEmail = (v) => (v || "").trim().length <= 254 && EMAIL_RE.test((v || "").trim());

// Keeps only digits (typing and pasting), capped at PHONE_MAX_DIGITS.
export const formatPhone = (v) => (v || "").replace(/\D/g, "").slice(0, PHONE_MAX_DIGITS);

// "1234567" -> "123-45-67" while typing; anything but digits is dropped.
export const formatRm = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 7);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

export const todayWib = (now = new Date()) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(now);

export const addDays = (isoDate, days) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

const cleanName = (v) => (v || "").replace(/\s+/g, " ").trim();

export const isValidPersonName = (v) => NAME_RE.test(cleanName(v));
export const DPJP_FORMAT_MSG = "DPJP wajib diisi (3-100 karakter; huruf, spasi, dan . , ' ( ) - saja)";

export function validatePengajuan(f, today = todayWib()) {
  const e = {};
  if (!NAME_RE.test(cleanName(f.nama_dokter))) e.nama_dokter = "Nama Dokter Pengaju wajib diisi (3-100 karakter; huruf, spasi, dan . , ' ( ) - saja)";
  if (!HP_DOKTER_RE.test(f.no_hp_dokter || "")) e.no_hp_dokter = "No. HP Dokter harus berupa angka saja, 10-12 digit, diawali 081-089 (contoh: 081212345678)";
  if (!isValidEmail(f.email_dokter)) e.email_dokter = EMAIL_FORMAT_MSG;
  const namaPasien = cleanName(f.nama_pasien);
  if (namaPasien.length > NAMA_PASIEN_MAX) e.nama_pasien = namaPasienPanjangMsg(namaPasien.length);
  else if (!NAME_RE.test(namaPasien)) e.nama_pasien = `Nama Pasien wajib diisi (3-${NAMA_PASIEN_MAX} karakter; huruf, spasi, dan . , ' ( ) - saja)`;
  if (!RM_RE.test(f.no_rm)) e.no_rm = "No. RM harus berformat XXX-XX-XX (contoh: 123-45-67)";
  const umur = String(f.umur || "").trim();
  if (!/^\d{1,3}$/.test(umur) || Number(umur) > 120) e.umur = "Umur harus berupa angka 0-120";
  if (!f.jenis_kelamin) e.jenis_kelamin = "Pilih jenis kelamin";
  if (!TELP_RE.test(f.no_telp || "")) e.no_telp = "No. Telp harus berupa angka saja, 8-12 digit, diawali 0 (contoh: 081212345678)";
  if (!f.espr) e.espr = "Pilih E-SPR";
  if (!["Sudah", "Belum"].includes(f.e_kmkb)) e.e_kmkb = "Pilih E-KMKB";
  if (!(f.los_pasien || "").trim()) e.los_pasien = "LOS Pasien wajib diisi (contoh: 3 hari / 5 hari; isi lama rawat saja, bukan catatan klinis)";
  if (!f.ksm_id) e.ksm_id = "Pilih Departemen";
  if (!isValidPersonName(f.dpjp_nama)) e.dpjp_nama = DPJP_FORMAT_MSG;
  if (!f.jaminan) e.jaminan = "Pilih jaminan";
  if (f.jaminan === "BPJS" && !["1", "2", "3"].includes(f.kelas_jaminan)) e.kelas_jaminan = "Pilih kelas jaminan";
  for (const [k, label] of [["diagnosa", "Diagnosa"], ["rencana_tindakan", "Rencana Tindakan"]]) {
    const len = (f[k] || "").trim().length;
    if (len < 3 || len > 500) e[k] = `${label} wajib diisi (3-500 karakter)`;
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.tanggal_operasi || "")) e.tanggal_operasi = "Tanggal operasi wajib diisi";
  else if (f.tanggal_operasi < today) e.tanggal_operasi = "Tanggal operasi tidak boleh sebelum hari ini";
  else if (f.tanggal_operasi > addDays(today, MAX_HARI_KE_DEPAN)) e.tanggal_operasi = `Tanggal operasi maksimal ${MAX_HARI_KE_DEPAN} hari dari hari ini`;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.tanggal_masuk_rawat_inap || "")) e.tanggal_masuk_rawat_inap = "Tanggal masuk rawat inap wajib diisi";
  else if (f.tanggal_masuk_rawat_inap < today) e.tanggal_masuk_rawat_inap = "Tanggal masuk rawat inap tidak boleh sebelum hari ini";
  else if (f.tanggal_masuk_rawat_inap > addDays(today, MAX_HARI_KE_DEPAN)) e.tanggal_masuk_rawat_inap = `Tanggal masuk rawat inap maksimal ${MAX_HARI_KE_DEPAN} hari dari hari ini`;
  return e;
}
